import { useEffect } from "react";
import { useToast } from "../../shared/ui";
import { useViewer } from "../../state/ViewerProvider";
import { useViewerEvent } from "../../state/hooks";
import { useCaps } from "./CapsProvider";
import { setNoticeSink, useDomainState, useUi } from "../../state/editor/useEditor";
import { LeftRail } from "./rails/LeftRail";
import { RightRail } from "./rails/RightRail";
import { LeftPanel } from "./panels/LeftPanel";
import { RightPanel } from "./inspector/RightPanel";
import { BasicPanel } from "./basic/BasicPanel";
import { AiMatchPanel } from "./basic/AiMatchPanel";
import { AI_PANEL, BASIC_PANEL } from "./basic/mode";
import { SceneToolbar } from "./toolbar/SceneToolbar";
import { ViewportPaneSwitches } from "./toolbar/ViewportPaneSwitches";
import { EmptyState } from "./toolbar/EmptyState";
import { DefaultMaterials } from "./materials/DefaultMaterialsDialog";
import { TopBarTools } from "./toolbar/TopBarTools";
import { ViewBar } from "./toolbar/ViewBar";
import { PresentMode } from "./PresentMode";
import { Icon } from "../kit";
import { ModelLoadOverlay, isLoadingModel, type ModelLoadStatus } from "./ModelLoadOverlay";
import { CARD_GAP, chromeInsets, type CardInset, type ChromeInsets } from "./layout";
import { LEFT_RAIL } from "./rails/railConfig";
import { StudioHud } from "./StudioGuide";
import { StudioKeys } from "./inspector/CommandPalette";
import { ConfiguratorBar } from "./configurator/ConfiguratorBar";
import { CustomerPreview } from "./configurator/CustomerPreview";
import { SceneFileBridge } from "./scene/SceneFileBridge";

/**
 * All editor chrome, overlaid on the canvas. The root is pointer-events-none so
 * the iframe behind it keeps receiving orbit input through the gaps; rails,
 * panels and the toolbar opt back in with pointer-events-auto.
 *
 * The chrome also reports its own width back to the shell (`onInsets`), which
 * pads `main` by that much so the canvas card shrinks instead of being covered.
 * Anything that wants to sit over the MODEL rather than over the whole row has
 * to be inset by the same amount — hence `cardInset` below.
 */
export function EditorChrome({
  onFullscreen,
  viewerOrigin,
  modelLoad = { phase: "idle" },
  onRetryModel,
  containerW = 0,
  onInsets,
  modelName,
}: {
  onFullscreen: () => void;
  viewerOrigin: string;
  /** Studio model-load progress; drives the canvas overlay. */
  modelLoad?: ModelLoadStatus;
  onRetryModel?: () => void;
  /** Width of the shell row, for the narrow-window fallback. */
  containerW?: number;
  /** Reports how much room the rails + open panels occupy. */
  onInsets?: (i: ChromeInsets) => void;
  /** The Vault model's name, when the studio was opened on one. */
  modelName?: string;
}) {
  const models = useDomainState("models");
  const selection = useDomainState("selection");
  const [ui, patchUi] = useUi();
  const loading = isLoadingModel(modelLoad);
  // The dropzone invites the user to supply a model. Showing it WHILE one is
  // being fetched (or after that fetch failed) is worse than showing nothing.
  const showEmpty = models.length === 0 && !loading && modelLoad.phase !== "error";

  // A selection-gated panel (Metals, Gems, …) left open when the selection is
  // cleared would keep reserving its 340px for an empty drawer, and `leftPanel`
  // is persisted, so it would survive a reload too. Close it — and read the
  // effective value for this frame so there is no gap before the effect runs.
  // Advanced only: Basic's Metals / Gems / Other share these panel ids but pick
  // their parts with chips, so they are never stranded by an empty selection.
  const openMeta = ui.inspectorMode === "advanced" ? LEFT_RAIL.find((i) => i.id === ui.leftPanel) : undefined;
  const stranded = !!openMeta?.requiresSelection && !selection;
  useEffect(() => {
    if (stranded) patchUi({ leftPanel: null });
  }, [stranded, patchUi]);

  const basic = ui.inspectorMode === "basic";
  // Narrow windows (a tablet at the counter): both side panels at once leave
  // a sliver of ring. Basic then shows one side at a time — a preset panel
  // open means Fine-tune steps aside, and closing it brings Fine-tune back.
  const narrow = containerW > 0 && containerW < 1100;
  const showFine = basic && !(narrow && !!ui.leftPanel);
  // In Basic the right side IS Fine-tune whenever it shows; `ui.rightTab` is
  // Advanced's state and must not decide Basic's layout.
  const insets = chromeInsets(
    stranded ? null : ui.leftPanel,
    basic ? (showFine ? BASIC_PANEL : null) : ui.rightTab,
    containerW,
    ui.inspectorMode,
  );

  // Errors from Basic, AI Match and the preset panels go through `notice()`.
  // Nothing used to listen, so every one of them failed silently.
  const { toast } = useToast();
  useEffect(() => {
    setNoticeSink((m) => toast({ tone: "error", title: m }));
    return () => setNoticeSink(null);
  }, [toast]);
  useEffect(() => {
    onInsets?.({ left: insets.left, right: insets.right });
  }, [insets.left, insets.right, onInsets]);

  /*
   * v60 — re-assert the saved split layout after the handshake.
   *
   * The viewer ALWAYS boots in `single`; the layout is host state and is
   * persisted, so without this a reload shows one viewport while the toolbar
   * button says quad. Gated on caps because the four method names do not exist
   * on an older viewer and RPC has no timeout.
   */
  const { client } = useViewer();
  const caps = useCaps();
  useEffect(() => {
    if (!caps.viewportQuadV60 || ui.viewportLayout !== "quad") return;
    // Layout only. Pane modes are session-only now, so a reload always lands in
    // the standard Top / Front / Right / 3D arrangement and there is nothing to
    // replay — which also removes the restore loop as a way to get out of sync.
    void client.ring.setViewportLayout("quad");
    // Deliberately once per mount: this restores the saved layout, it does not
    // mirror it. Re-running on every ui change would fight the user's clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caps.viewportQuadV60]);

  // The viewer owns which pane is active once the user clicks into one.
  useViewerEvent("viewportLayoutChanged", (p: { state: { active: string; layout: string } }) => {
    if (p?.state?.active && p.state.active !== ui.activePane) {
      patchUi({ activePane: p.state.active as never });
    }
  });

  /** Box of the canvas card, for overlays that belong over the model. */
  // The canvas card is full-bleed now — panels overlay it instead of insetting
  // it — so anything positioned over the MODEL measures from the constant gap,
  // not from the chrome width. The split-viewport pane switches depend on this:
  // their quarters must match the quarters the viewer actually scissors, and the
  // viewer knows nothing about which panels are open.
  const cardInset: CardInset = {
    left: CARD_GAP,
    right: CARD_GAP,
    top: CARD_GAP,
    bottom: CARD_GAP,
  };

  return (
    // z-[99]: the panels now float OVER the canvas rather than shrinking it, so
    // they have to win the stacking contest against the canvas card (which sets
    // `isolation: isolate` for WebKit's iframe clipping) and the loading overlay.
    <div className="pointer-events-none absolute inset-0 z-[99]">
      {/* Reads the SAME ui.watermark slice the export compositor reads, so the
          two can never disagree. It used to be chrome-only: visible here,
          present in exactly zero exported pixels. */}
      {ui.watermark.on && ui.watermark.text ? (
        <div
          className="pointer-events-none absolute z-[4] flex justify-center"
          style={{
            left: cardInset.left,
            right: cardInset.right,
            bottom: cardInset.bottom + 24,
          }}
        >
          <span
            className="select-none text-[13px] font-semibold tracking-wide"
            style={{ color: ui.watermark.light ? "rgba(255,255,255,0.5)" : "rgba(60,50,30,0.4)" }}
          >
            {ui.watermark.text}
          </span>
        </div>
      ) : null}
      {showEmpty ? <EmptyState viewerOrigin={viewerOrigin} inset={cardInset} /> : null}
      <ModelLoadOverlay status={modelLoad} onRetry={onRetryModel} inset={cardInset} />
      <TopBarTools modelName={modelName} />
      <StudioKeys />
      <PresentMode />
      {/* Preview as customer: the shopper's bar over the canvas, the studio's
          own chrome hidden (as Present hides it) until Exit puts it all back. */}
      <CustomerPreview modelName={modelName} />
      {/* v65 scene files: restores the studio's half of a scene the viewer
          applied on its own (a "Ring + scene" model), and covers the canvas
          while one is being opened. */}
      <SceneFileBridge />
      {ui.present || ui.customerPreview ? null : (
        <>
          {/* "Apply default materials" for a newly loaded piece. A centred modal
              portalled to <body>: it never moves or resizes the canvas. */}
          <DefaultMaterials />
          <StudioHud insets={insets} />
          {/* v60: the per-pane lock switches sit over the pane corners. Host DOM on
              top of the iframe receives its own clicks; only clicks that land ON the
              iframe are invisible to the host. */}
          <ViewportPaneSwitches inset={cardInset} />
          <ConfiguratorBar insets={insets} />
          <LeftRail />
          <LeftPanel />
          {basic ? (
            <>
              {models.length > 0 && !(narrow && ui.leftPanel && containerW < 800) ? <ViewBar insets={insets} /> : null}
              {models.length > 0 && !(narrow && ui.leftPanel) ? (
                <button
                  onClick={() => patchUi({ present: true })}
                  className="pointer-events-auto absolute top-[26px] z-20 inline-flex h-[34px] items-center gap-1.5 rounded-xl border border-ed-border bg-ed-panel px-3 text-[13px] font-medium text-ed-text shadow-[0_6px_20px_rgba(0,0,0,0.08)] hover:bg-ed-hover"
                  style={{ right: insets.right + 26 }}
                  title="Full screen, spinning, for showing a customer"
                  data-present-open
                >
                  <Icon name="slideshow" size={18} />
                  Present
                </button>
              ) : null}
              {showFine ? <BasicPanel /> : null}
            </>
          ) : (
            <>
              <SceneToolbar onFullscreen={onFullscreen} rightInset={insets.right} />
              <RightPanel />
              {ui.rightTab === AI_PANEL ? <AiMatchPanel /> : null}
              <RightRail />
            </>
          )}
        </>
      )}
    </div>
  );
}
